import React from "react";
import Image from "next/image";
import {
  Clock,
  Building,
  Wifi,
  ExternalLink,
  Mail,
  DollarSign,
  Briefcase,
  MapPin,
  Plane,
  Check,
  File,
  Eye,
  // Image,
} from "lucide-react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { createApplication } from "@src/graphql/mutations";
import { useAuth } from "@src/auth";
import { getJobSeekerIdForUser } from "queries";
import DefaultCompany from "@assets/default_company.png";
import { renderDatePostedForPost } from "@src/helpers/jobsearch";
import Tag from "@components/JobsViewer/Tag";
import parse from "@src/helpers/parseHTML";
import styles from "./style.module.css";
import AppLink from "@components/JobsViewer/AppLink";
import CoverLetterButton from "@components/JobsViewer/CoverLetterButton";
import ResumeLink from "../JobsViewer/ResumeLink";

const Badge = ({ icon: Icon, text, color = "default" }) => {
  return (
    <div className={`${styles.tag} ${styles[color]}`}>
      {Icon && <Icon size={12} className={styles.tagIcon} />}
      <span className={styles.tagText}>{text}</span>
    </div>
  );
};

export default function MobileApplicationCard({
  post,
  onClick,
  empty,
  dashboardCard,
}) {
  const { user } = useAuth();
  const [apply, { loading: applicationLoading }] = useMutation(
    gql(createApplication)
  );

  const { data: userData, loading } = useQuery(gql(getJobSeekerIdForUser), {
    variables: { userId: user?.username },
  });

  const jobSeekerId = userData?.getUserById?.jobSeeker?.id;

  const applyToJob = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (jobSeekerId) {
      const vars = {
        variables: {
          input: {
            id: (Math.random() * 1000).toString(),
            jobId: post?.id,
            jobSeekerId,
          },
        },
      };

      apply(vars);
    }
  };

  if (empty || loading) {
    return (
      <div className={styles.mobileCardContainer}>
        <div className={styles.loadingCard}>
          <div className={styles.loadingTitle} />
          <div className={styles.loadingCompany} />
          <div className={styles.loadingDescription} />
          <div className={styles.loadingDescription} />
        </div>
      </div>
    );
  }

  const {
    description,
    jobTitle,
    companyName,
    jobLogo,
    startSalaryRangeThousands,
    endSalaryRangeThousands,
    roleType,
    remotePolicy,
    digitalNomadFriendly,
    willSponsor,
    officeLocations,
    companyProfile,
  } = post;

  const coImage = post?.companyProfile?.image ?? DefaultCompany;
  // const parsedDescription = description;

  console.log("datePosted", renderDatePostedForPost(post));
  console.log("post", post);

  return (
    <div className={styles.mobileCardContainer} onClick={onClick}>
      <div className={styles.innerWrapper}>
        <div
          className={`${styles.row} ${
            !dashboardCard ? styles.dashboardRow : ""
          }`}
        >
          {console.log("coImage", coImage)}
          {console.log("jobLogo", jobLogo)}
          <div className={styles.logo}>
            <Image
              src={jobLogo ?? coImage.src}
              alt={companyName}
              width={70}
              height={70}
              objectFit="contain" // Keeps the image within bounds without stretching
            />
          </div>
          <div
            className={`${styles.text} ${
              !dashboardCard ? styles.textWrapper : ""
            }`}
          >
            <div className={styles.title}>{jobTitle}</div>
            <div className={styles.companyName}>{companyName}</div>
            <div className={styles.datePosted}>
              <Clock size={16} /> {renderDatePostedForPost(post)}
            </div>
          </div>
        </div>
        {dashboardCard && (
          <div className={styles.appLink}>
            <AppLink
              dashboardCard={dashboardCard}
              post={post}
              mini
              icon={
                <Eye
                  size={24}
                  className={styles.actionButton}
                  style={{ width: "24px", height: "24px", display: "block" }}
                />
              }
            />
            <CoverLetterButton
              post={post}
              dashboardCard
              mini
              icon={
                <Mail
                  size={24}
                  className={styles.actionButton}
                  style={{ width: "24px", height: "24px", display: "block" }}
                />
              }
            />

            <ResumeLink
              post={post} 
              dashboardCard 
              mini
              icon={
                <File
                  size={24}
                  className={styles.actionButton}
                  style={{ width: "24px", height: "24px", display: "block" }}
                />
              }              
            />
          </div>
        )}
      </div>
      <div className={styles.footer}>
        {/* <div className={styles.datePosted}>{renderDatePostedForPost(post)}</div> */}
        {console.log("officeLocations", officeLocations)}
        {console.log("Salary", startSalaryRangeThousands)}
        {console.log("roleType", roleType)}
        {console.log("remotePolicy", remotePolicy)}
        <div className={styles.smallLabelsContainer}>
          {officeLocations?.length > 0 && (
            <Badge
              icon={MapPin}
              text={
                officeLocations.length > 1
                  ? `${officeLocations.length} Locations`
                  : officeLocations[0].formatted_address
              }
              color="orange"
            />
          )}
          {startSalaryRangeThousands > 0 && (
            <Badge
              icon={DollarSign}
              text={
                endSalaryRangeThousands
                  ? `$${startSalaryRangeThousands}k - $${endSalaryRangeThousands}k`
                  : `$${startSalaryRangeThousands}k`
              }
              color="green"
            />
          )}

          {roleType && (
            <Badge
              icon={Briefcase}
              text={roleType.charAt(0) + roleType.slice(1).toLowerCase()}
              color="teal"
            />
          )}

          {remotePolicy && (
            <Badge
              icon={remotePolicy === "ONSITE" ? Building : Wifi}
              text={
                remotePolicy === "FULLREMOTE"
                  ? "100% Remote"
                  : remotePolicy.charAt(0) +
                    remotePolicy
                      .slice(1)
                      .toLowerCase()
                      .replace("remote", " Remote")
              }
              color={remotePolicy === "ONSITE" ? "grey" : "blue"}
            />
          )}

          {digitalNomadFriendly && (
            <Badge icon={Plane} text="Nomad Friendly" color="green" />
          )}

          {willSponsor && (
            <Badge icon={Check} text="Will Sponsor VISA" color="green" />
          )}
        </div>
      </div>
    </div>
  );
}

export const getApplicationLabels = (postData, standardWidth, blackwhite) => {
  const labels = [];

  const {
    startSalaryRangeThousands: startRange,
    endSalaryRangeThousands: endRange,
  } = postData;

  if (startRange) {
    let salaryText = "";

    if (startRange && endRange) {
      salaryText = `$${startRange}k - $${endRange}k`;
    } else {
      salaryText = `$${startRange}k`;
    }

    labels.push(
      <Tag
        key={`${postData?.id}-parttime`}
        standardWidth={standardWidth}
        color="green"
        icon="dollar sign"
        text={salaryText}
        doNotTrim
      />
    );
  }

  if (postData?.scrapedSource === "yc") {
    labels.push(
      <Tag
        key={`${postData?.id}-yc`}
        standardWidth={standardWidth}
        color="teal"
        icon="YC"
        text={"YCombinator"}
      />
    );
  }

  switch (postData?.roleType) {
    case "PARTTIME":
      labels.push(
        <Tag
          key={`${postData?.id}-parttime`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={blackwhite ? "grey" : "teal"}
          icon="hourglass"
          text={"Part Time"}
        />
      );
      break;
    case "FULLTIME":
      labels.push(
        <Tag
          key={`${postData?.id}-fulltime`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={blackwhite ? "grey" : "teal"}
          icon="hourglass"
          text={"Full Time"}
        />
      );
      break;
    case "CONTRACT":
      labels.push(
        <Tag
          key={`${postData?.id}-contract`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={blackwhite ? "grey" : "teal"}
          icon="hourglass"
          text={"Contract"}
        />
      );
      break;
    default:
      break;
  }

  switch (postData?.remotePolicy) {
    case "HYBRIDREMOTE":
      labels.push(
        <Tag
          key={`${postData?.id}-hybrid`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={!blackwhite ? "grey" : "teal"}
          icon="wifi"
          text="Hybrid Remote"
        />
      );
      break;
    case "FULLREMOTE":
      labels.push(
        <Tag
          key={`${postData?.id}-fullremote`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={!blackwhite ? "grey" : "teal"}
          icon="wifi"
          text="100% Remote"
        />
      );
      break;
    case "ONSITE":
      labels.push(
        <Tag
          key={`${postData?.id}-onsite`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={!blackwhite ? "grey" : "black"}
          icon="building outline"
          text="Onsite"
        />
      );
      break;
    case "REMOTEOPTIONAL":
      labels.push(
        <Tag
          key={`${postData?.id}-optional`}
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          color={!blackwhite ? "grey" : "teal"}
          icon="wifi"
          text="Remote Optional"
        />
      );
      break;
    default:
      break;
  }

  if (postData?.digitalNomadFriendly) {
    labels.push(
      <Tag
        key={`${postData?.id}-dnFriendly`}
        blackwhite={blackwhite}
        standardWidth={standardWidth}
        color="green"
        icon="plane"
        text="Nomad Friendly"
      />
    );
  }

  if (postData?.willSponsor) {
    labels.push(
      <Tag
        key={`${postData?.id}-willSponsor`}
        blackwhite={blackwhite}
        standardWidth={standardWidth}
        color="green"
        icon="check"
        text="Will Sponsor VISA"
      />
    );
  }

  if (postData?.officeLocations?.length) {
    if (postData?.officeLocations?.length > 1) {
      const locs = postData?.officeLocations;
      labels.push(
        <Tag
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          key={`${postData?.id}-loc`}
          color="orange"
          icon="map pin"
          text={`${locs.length} Locations`}
        />
      );
    } else {
      labels.push(
        <Tag
          blackwhite={blackwhite}
          standardWidth={standardWidth}
          key={`${postData?.id}-${postData?.officeLocations[0]?.formatted_address}`}
          color="orange"
          icon="map pin"
          text={postData?.officeLocations[0]?.formatted_address}
        />
      );
    }
  }

  return labels;
};
